/**
 * File: ModernStatsSection.jsx
 * Path: /src/components/common/ModernStatsSection.jsx
 * Description: Reusable modern stats section with animated background
 * Key Features: Gradient background, animated waves, and consistent branding
 */

import React from 'react';
import { motion } from 'framer-motion';

const ModernStatsSection = ({ 
  stats = [
    { number: '85%', label: 'Time Saved on Research' },
    { number: '70%', label: 'Faster Contract Analysis' },
    { number: '92%', label: 'Client Satisfaction' },
    { number: '3x', label: 'Return on Investment' }
  ],
  title = "",
  description = ""
}) => {
  return (
    <section className="py-16 relative overflow-hidden">
      {/* Main background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-800 to-secondary-900 z-0"></div>
      
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-10 z-0">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>
      
      {/* Glow effects */}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/3 bg-primary-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/3 bg-secondary-500/20 rounded-full blur-3xl"></div>
      
      {/* Content */}
      <div className="container-padding mx-auto relative z-10">
        {title && (
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">{title}</h2>
            {description && <p className="text-lg text-primary-100">{description}</p>}
          </motion.div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div 
              key={index} 
              className="text-center glass-effect p-6 rounded-xl border border-white/10 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="text-4xl md:text-5xl font-bold mb-2 text-white">{stat.number}</div>
              <div className="text-primary-100">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ModernStatsSection;